import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './styles/global.css';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import StatsBar from './components/StatsBar';
import FilterBar from './components/FilterBar';
import OrderTable from './components/OrderTable';
import AssignModal from './components/AssignModal';
import HistoryModal from './components/HistoryModal';

const API = process.env.REACT_APP_API_URL || '';

const SERVICE_TYPES = [
  { code: 'conv', label: 'Conversion', color: '#3182ce' },
  { code: 'tp', label: 'Treatment Plan', color: '#dd6b20' },
  { code: 'sg', label: 'Surgical Guide', color: '#805ad5' },
  { code: 'rep', label: 'Report', color: '#e53e3e' },
  { code: 'mod', label: 'Model Reg.', color: '#38a169' },
  { code: 'soft', label: 'Software', color: '#718096' },
  { code: 'vr', label: 'Voucher', color: '#d69e2e' },
  { code: 'misc', label: 'Misc', color: '#4a5568' },
  { code: 'restTemp', label: 'Temp Rest.', color: '#00b5d8' },
  { code: 'restFinal', label: 'Final Rest.', color: '#319795' },
  { code: 'GFMR', label: 'GFMR', color: '#9f7aea' },
  { code: 'FMP', label: 'FMP', color: '#ed64a6' },
  { code: 'other', label: 'Other', color: '#a0aec0' },
];

const TIME_PERIODS = [
  { value: 1, label: 'Today' },
  { value: 7, label: 'Week' },
  { value: 30, label: 'Month' },
  { value: 60, label: '2 Mo' },
  { value: 90, label: '3 Mo' },
  { value: 180, label: '6 Mo' },
];

export default function App() {
  const [orders, setOrders] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [stats, setStats] = useState({ total: 0, assigned: 0, unassigned: 0, rush: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTypes, setActiveTypes] = useState(
    SERVICE_TYPES.reduce((acc, t) => ({ ...acc, [t.code]: true }), {})
  );
  const [timePeriod, setTimePeriod] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState('services');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [assignModal, setAssignModal] = useState(null);
  const [historyModal, setHistoryModal] = useState(null);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/orders`).then(r => r.json()),
      fetch(`${API}/designers`).then(r => r.json()),
      fetch(`${API}/stats/1`).then(r => r.json()),
    ]).then(([ordersData, designersData, statsData]) => {
      setOrders(ordersData);
      setDesigners(designersData);
      setStats(statsData);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const filteredOrders = useMemo(() => {
    let result = orders;
    result = result.filter(order =>
      order.subOrders.some(sub => activeTypes[sub.typeCode])
    );
    if (timePeriod) {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - timePeriod);
      result = result.filter(order => new Date(order.receivedTime) >= cutoff);
    }
    if (statusFilter === 'notFinished') {
      result = result.filter(order =>
        order.subOrders.some(sub => ![8, 50].includes(sub.statusId))
      );
    } else if (statusFilter === 'pending') {
      result = result.filter(order =>
        order.subOrders.some(sub => [9, 14, 24, 41, 110, 127, 140, 147].includes(sub.statusId))
      );
    } else if (statusFilter === 'expired') {
      result = result.filter(order =>
        order.subOrders.some(sub => sub.statusId === 52)
      );
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(order =>
        order.patientName.toLowerCase().includes(q) ||
        order.doctor.toLowerCase().includes(q) ||
        order.scanCenter.toLowerCase().includes(q) ||
        String(order.id).includes(q)
      );
    }
    result = [...result].sort((a, b) => {
      const { key, direction } = sortConfig;
      let valA = a[key]; let valB = b[key];
      if (typeof valA === 'string') { valA = valA.toLowerCase(); valB = (valB || '').toLowerCase(); }
      if (valA < valB) return direction === 'asc' ? -1 : 1;
      if (valA > valB) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [orders, activeTypes, timePeriod, statusFilter, searchQuery, sortConfig]);

  const pageSize = 50;
  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / pageSize));
  const paginatedOrders = filteredOrders.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleSort = useCallback((key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  const handleAssign = useCallback((orderId, subOrderId, designerId) => {
    const designer = designers.find(d => d.id === designerId);
    setOrders(prev => prev.map(order => {
      if (order.id !== orderId) return order;
      return {
        ...order,
        subOrders: order.subOrders.map(sub => {
          if (sub.id !== subOrderId) return sub;
          return { ...sub, assignedTo: designerId, assignedToName: designer?.name || null };
        })
      };
    }));
    setStats(prev => ({
      ...prev,
      assigned: prev.assigned + (designerId ? 1 : 0),
      unassigned: prev.unassigned - (designerId ? 1 : 0),
    }));
    setAssignModal(null);
  }, [designers]);

  const toggleServiceType = useCallback((code) => {
    setActiveTypes(prev => ({ ...prev, [code]: !prev[code] }));
    setCurrentPage(1);
  }, []);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
        <p>Loading Order Flow Dashboard...</p>
      </div>
    );
  }

  return (
    <div className={`app-layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <Sidebar
        activeTab={activeTab}
        onTabChange={setActiveTab}
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(prev => !prev)}
      />
      <div className="main-content">
        <Header
          orderCount={filteredOrders.length}
          serviceCount={filteredOrders.reduce((acc, o) => acc + o.subOrders.filter(s => activeTypes[s.typeCode]).length, 0)}
          activeTab={activeTab}
        />
        {activeTab === 'services' && (
          <>
            <StatsBar stats={stats} />
            <FilterBar
              serviceTypes={SERVICE_TYPES}
              activeTypes={activeTypes}
              onToggleType={toggleServiceType}
              timePeriods={TIME_PERIODS}
              timePeriod={timePeriod}
              onTimePeriodChange={(v) => { setTimePeriod(v); setCurrentPage(1); }}
              statusFilter={statusFilter}
              onStatusFilterChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
            />
            <OrderTable
              orders={paginatedOrders}
              activeTypes={activeTypes}
              sortConfig={sortConfig}
              onSort={handleSort}
              designers={designers}
              onAssign={(orderId, subOrderId) => setAssignModal({ orderId, subOrderId })}
              onViewHistory={(orderId, serviceId) => setHistoryModal({ orderId, serviceId })}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              totalOrders={filteredOrders.length}
            />
          </>
        )}
        {activeTab === 'printers' && (
          <div className="placeholder-tab">
            <div className="placeholder-icon">&#128424;</div>
            <h2>Printers & Accessories</h2>
            <p>3D printer orders and accessories management</p>
          </div>
        )}
        {activeTab === 'ismile' && (
          <div className="placeholder-tab">
            <div className="placeholder-icon">&#128522;</div>
            <h2>iSmile Cases</h2>
            <p>iSmile case management interface</p>
          </div>
        )}
      </div>
      {assignModal && (
        <AssignModal
          orderId={assignModal.orderId}
          subOrderId={assignModal.subOrderId}
          designers={designers}
          onAssign={handleAssign}
          onClose={() => setAssignModal(null)}
        />
      )}
      {historyModal && (
        <HistoryModal
          orderId={historyModal.orderId}
          serviceId={historyModal.serviceId}
          onClose={() => setHistoryModal(null)}
        />
      )}
    </div>
  );
}
